import {
  collection, deleteDoc, doc, onSnapshot, serverTimestamp, setDoc,
} from 'firebase/firestore';
import { deleteObject, getBytes, ref, uploadBytes } from 'firebase/storage';
import { nanoid } from 'nanoid';
import { db, storage } from './index';

const TREE_ID = 'main';
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

const treeRefFor = (userId) => doc(db, 'users', userId, 'familyTrees', TREE_ID);
const membersRefFor = (userId) => collection(treeRefFor(userId), 'members');

export async function loadFamilyMemberPhoto(userId, photo) {
  const expectedPrefix = `familyTrees/${userId}/${TREE_ID}/`;
  if (!photo?.path?.startsWith(expectedPrefix)) {
    throw new Error('The saved photo does not belong to this family tree.');
  }
  const file = await getBytes(ref(storage, photo.path), MAX_PHOTO_BYTES);
  return URL.createObjectURL(new Blob([file], { type: photo.contentType || 'image/jpeg' }));
}

export const DEFAULT_FAMILY_TREE_SETTINGS = {
  surname: '',
  treeStyle: 'classic',
  dateFormat: 'DD/MM/YYYY',
  fontSize: 'medium',
};

export function subscribeFamilyTree(userId, { onSettings, onMembers, onError }) {
  const unsubscribeSettings = onSnapshot(
    treeRefFor(userId),
    (snapshot) => onSettings(snapshot.exists()
      ? { ...DEFAULT_FAMILY_TREE_SETTINGS, ...snapshot.data() }
      : DEFAULT_FAMILY_TREE_SETTINGS),
    onError,
  );
  const unsubscribeMembers = onSnapshot(
    membersRefFor(userId),
    (snapshot) => onMembers(snapshot.docs.map((member) => ({ id: member.id, ...member.data() }))),
    onError,
  );
  return () => {
    unsubscribeSettings();
    unsubscribeMembers();
  };
}

export async function saveFamilyTreeSettings(userId, settings) {
  await setDoc(treeRefFor(userId), {
    surname: settings.surname.trim().slice(0, 80),
    treeStyle: ['classic', 'modern', 'compact'].includes(settings.treeStyle) ? settings.treeStyle : 'classic',
    dateFormat: ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'].includes(settings.dateFormat)
      ? settings.dateFormat : 'DD/MM/YYYY',
    fontSize: ['small', 'medium', 'large'].includes(settings.fontSize) ? settings.fontSize : 'medium',
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function saveFamilyMember(userId, member, photoFile, originalPhoto = member.photo) {
  const id = member.id || nanoid();
  const memberRef = doc(membersRefFor(userId), id);
  let photo = member.photo || null;
  let uploadedPhotoRef = null;

  if (photoFile) {
    if (!photoFile.type.startsWith('image/')) throw new Error('Choose an image file for the photo.');
    if (photoFile.size > MAX_PHOTO_BYTES) throw new Error('Photos must be 5 MB or smaller.');
    const photoPath = `familyTrees/${userId}/${TREE_ID}/${id}/${nanoid()}`;
    uploadedPhotoRef = ref(storage, photoPath);
    try {
      await uploadBytes(uploadedPhotoRef, photoFile, { contentType: photoFile.type });
      photo = { path: photoPath, contentType: photoFile.type };
    } catch (error) {
      if (uploadedPhotoRef) {
        try {
          await deleteObject(uploadedPhotoRef);
        } catch (cleanupError) {
          console.error('Could not clean up an incomplete family photo upload:', cleanupError);
        }
      }
      throw error;
    }
  }

  const data = {
    firstName: member.firstName.trim().slice(0, 80),
    surname: member.surname.trim().slice(0, 80),
    birthDate: member.birthDate || '',
    deathDate: member.deathDate || '',
    countryOfBirth: member.countryOfBirth.trim().slice(0, 100),
    profession: member.profession.trim().slice(0, 100),
    facts: member.facts.trim().slice(0, 2000),
    parentIds: [...new Set(member.parentIds || [])].filter((parentId) => parentId !== id),
    spouseIds: [...new Set(member.spouseIds || [])].filter((spouseId) => spouseId !== id),
    photo,
    updatedAt: serverTimestamp(),
  };

  try {
    await setDoc(memberRef, data);
  } catch (error) {
    if (uploadedPhotoRef) {
      try {
        await deleteObject(uploadedPhotoRef);
      } catch (cleanupError) {
        console.error('Could not clean up an unsaved family photo:', cleanupError);
      }
    }
    throw error;
  }

  let photoCleanupFailed = false;
  if (originalPhoto?.path && originalPhoto.path !== photo?.path) {
    try {
      await deleteObject(ref(storage, originalPhoto.path));
    } catch {
      photoCleanupFailed = true;
    }
  }
  return { id, photoCleanupFailed };
}

export async function deleteFamilyMember(userId, member) {
  await deleteDoc(doc(membersRefFor(userId), member.id));
  if (!member.photo?.path) return { photoCleanupFailed: false };
  try {
    await deleteObject(ref(storage, member.photo.path));
    return { photoCleanupFailed: false };
  } catch {
    return { photoCleanupFailed: true };
  }
}
